import React, { useEffect } from 'react';
import TechnicianSelect from './TechnicianSelect';
import { equipeDaEtapa, definirNaEtapa, pessoa } from '../../../utils/equipe';

/**
 * A equipe de uma etapa dentro do workflow: o que o cadastro planejou e como
 * gravar a mudança de volta no serviço (`workflowData.equipe_das_etapas`).
 *
 * O `onUpdate` recebe função para partir do valor mais novo — DG30 e DG60
 * podem gravar no mesmo instante.
 */
export const equipeNoWorkflow = (workflowData, onUpdate, etapaId) => {
  const equipe = equipeDaEtapa(workflowData?.equipe_das_etapas, etapaId);
  const mudar = (papel) => (id, nome) => onUpdate('equipe_das_etapas',
    (anterior) => definirNaEtapa(anterior, etapaId, papel, id, nome));
  return {
    planejado: pessoa(equipe, 'responsavel'),
    ajudante: pessoa(equipe, 'ajudante'),
    responsavelNaEquipe: mudar('responsavel'),
    ajudanteNaEquipe: mudar('ajudante'),
  };
};

/**
 * Responsável e ajudante de uma etapa, lado a lado.
 *
 * Devolve um fragmento — os dois selects ocupam duas células do grid de quem
 * o usa, e cada aba já tem o seu grid de cabeçalho.
 *
 * `responsavel` é quem aparece no primeiro select. Nas abas que gravam técnico
 * na sessão (protocolo, TE, DG), é o da sessão, porque é ele que o relatório
 * e a conferência leem. `planejado` é o que o cadastro do serviço já escolheu:
 * etapa ainda sem técnico na sessão nasce com ele, em vez de pedir a mesma
 * escolha duas vezes.
 */
const EquipeDaEtapa = ({
  laboratoryId,
  responsavel = null,
  ajudante = null,
  planejado = null,
  onResponsavel,
  onAjudante,
  rotuloResponsavel = 'Responsável',
  obrigatorio = true,
  dica,
  className,
}) => {
  const vazio = !responsavel?.id && !responsavel?.nome;

  useEffect(() => {
    if (vazio && (planejado?.id || planejado?.nome)) onResponsavel(planejado.id, planejado.nome);
    // Só quando o vazio aparece: quem limpar à mão de propósito, limpa de novo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vazio, planejado?.id, planejado?.nome]);

  return (
    <>
      <TechnicianSelect
        label={rotuloResponsavel}
        laboratoryId={laboratoryId}
        technicianId={responsavel?.id || null}
        technicianName={responsavel?.nome || ''}
        onChange={onResponsavel}
        obrigatorio={obrigatorio}
        dica={dica}
        className={className}
      />
      <TechnicianSelect
        label="Ajudante"
        laboratoryId={laboratoryId}
        technicianId={ajudante?.id || null}
        technicianName={ajudante?.nome || ''}
        onChange={onAjudante}
        obrigatorio={false}
        excluirId={responsavel?.id || null}
        className={className}
      />
    </>
  );
};

export default EquipeDaEtapa;
